import React, {FC, Fragment} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {ResourceMap} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownEmpty} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownError} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownError';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownSeparator} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {
  Anchor,
  buildReferenceObject,
} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {computeCustomViewDescription} from '../../../helpers/models/customViewHelpers';
import {
  SettingsSearchChannelModel,
  SettingsSearchCompanyPreferenceModel,
  SettingsSearchCompanyPreferenceRouteModel,
  SettingsSearchCompanySettingRouteModel,
  SettingsSearchCustomViewModel,
  SettingsSearchDelegationModel,
  SettingsSearchImportRouteModel,
  SettingsSearchInboxModel,
  SettingsSearchIntegrationModel,
  SettingsSearchItemModel,
  SettingsSearchItemTypesEnum,
  SettingsSearchPayloadModel,
  SettingsSearchPersonalPreferenceModel,
  SettingsSearchRoleModel,
  SettingsSearchRouteItemModel,
  SettingsSearchRuleModel,
  SettingsSearchSettingRouteModel,
  SettingsSearchSignatureModel,
  SettingsSearchTagModel,
  SettingsSearchTeammateModel,
  SettingsSearchTeammateRouteModel,
  SettingsSearchTeamModel,
  SettingsSearchVacationResponderModel,
} from '../../../models/api/settingsSearchModel';
import {RuleParametersDependencies} from '../../../settings/rules/ruleConditionModel';
import {SettingsSearchSeeAllHandler, SettingsSearchSelectItemHandler} from './settingsSearchCommonTypes';
import {SettingsSearchDropdownChannelItem} from './settingsSearchDropdownChannelItem';
import {SettingsSearchDropdownDelegationItem} from './settingsSearchDropdownDelegationItem';
import {SettingsSearchDropdownImportItem} from './settingsSearchDropdownImportItem';
import {SettingsSearchDropdownInboxItem} from './settingsSearchDropdownInboxItem';
import {SettingsSearchDropdownIntegrationItem} from './settingsSearchDropdownIntegrationItem';
import {SettingsSearchDropdownLoadingItem} from './settingsSearchDropdownLoadingItem';
import {SettingsSearchDropdownPreferenceItem} from './settingsSearchDropdownPreferenceItem';
import {SettingsSearchDropdownResponderItem} from './settingsSearchDropdownResponderItem';
import {SettingsSearchDropdownRoleItem} from './settingsSearchDropdownRoleItem';
import {SettingsSearchDropdownRuleItem} from './settingsSearchDropdownRuleItem';
import {SettingsSearchDropdownSeeAllItem} from './settingsSearchDropdownSeeAllItem';
import {SettingsSearchDropdownSignatureItem} from './settingsSearchDropdownSignatureItem';
import {SettingsSearchDropdownTagItem} from './settingsSearchDropdownTagItem';
import {SettingsSearchDropdownTeamItem} from './settingsSearchDropdownTeamItem';
import {SettingsSearchDropdownTeammateItem} from './settingsSearchDropdownTeammateItem';
import {SettingsSearchDropdownViewItem} from './settingsSearchDropdownViewItem';

/*
 * Props.
 */

interface SettingsSearchDropdownProps {
  anchor: Anchor;
  searchResults: SettingsSearchPayloadModel | null | undefined;
  onSeeAllClick: SettingsSearchSeeAllHandler;
  onSelectItem: SettingsSearchSelectItemHandler;
  user: TeammateModel;
  teamsMap: ResourceMap<TeamModel>;
  isStillLoading: boolean;
  hasError: boolean;
  ruleParametersDependencies: RuleParametersDependencies;
}

/*
 * Style.
 */

const StyledDropdown = styled(Dropdown)`
  z-index: 2;
  max-height: calc(100vh - 100px);
  max-height: calc(100svh - 100px);
`;

/*
 * Component.
 */

export const SettingsSearchDropdown: FC<SettingsSearchDropdownProps> = (props) => {
  const {searchResults, isStillLoading} = props;
  const emptyResults = searchResults && searchResults.length === 0;
  const isRenderingEmptyState = !isStillLoading && emptyResults;
  const anchorObject = buildReferenceObject(props.anchor);
  const localize = useLocalize();

  return (
    <StyledDropdown
      isExclusive={false}
      height={isRenderingEmptyState ? 206 : undefined}
      anchor={props.anchor}
      width={anchorObject.getBoundingClientRect().width}
    >
      <DropdownList shouldSelectFirst>{renderResults(props, localize)}</DropdownList>
    </StyledDropdown>
  );
};

function renderResults(props: SettingsSearchDropdownProps, localize: Localizer) {
  const {searchResults, isStillLoading, hasError} = props;
  const hasResults = searchResults && searchResults.length > 0;
  const emptyResults = searchResults && searchResults.length === 0;

  if (hasError) {
    return <DropdownError />;
  }

  if (isStillLoading) {
    return renderLoading();
  }

  // If we our results array exists but is empty, then the server didn't find anything
  if (emptyResults) {
    return <DropdownEmpty />;
  }

  // If we have no results array, then we don't have a query and don't need to render anything
  if (!hasResults) {
    return null;
  }

  if (!searchResults) {
    return null;
  }

  return searchResults
    .filter((payload) => !(payload.items.length === 0 && !payload.seeAllItem))
    .map((resultSection, index, array) => {
      const sectionRender = renderResultSection(resultSection, props, localize);
      return (
        <Fragment key={resultSection.resourceType}>
          {sectionRender}
          {maybeRenderSeeAllItems(resultSection, props)}
          {index + 1 < array.length && <DropdownSeparator />}
        </Fragment>
      );
    });
}

function renderLoading() {
  return (
    <>
      <SettingsSearchDropdownLoadingItem />
      <SettingsSearchDropdownLoadingItem />
      <SettingsSearchDropdownLoadingItem />
      <SettingsSearchDropdownLoadingItem />
    </>
  );
}

function renderResultSection(
  resultSection: SettingsSearchItemModel,
  props: SettingsSearchDropdownProps,
  localize: Localizer,
) {
  switch (resultSection.resourceType) {
    case SettingsSearchItemTypesEnum.COMPANY_PREFERENCE:
      return renderCompanyPreference(resultSection, props);

    case SettingsSearchItemTypesEnum.CUSTOM_VIEW:
      return renderCustomViews(resultSection, props, localize);

    case SettingsSearchItemTypesEnum.INBOX:
      return renderInboxes(resultSection, props);

    case SettingsSearchItemTypesEnum.IMPORT_ROUTE:
      return renderImports(resultSection, props);

    case SettingsSearchItemTypesEnum.INTEGRATION:
      return renderIntegrations(resultSection, props);

    case SettingsSearchItemTypesEnum.PERSONAL_PREFERENCE:
      return renderTeammatePreferences(resultSection, props);

    case SettingsSearchItemTypesEnum.ROLE:
      return renderRoles(resultSection, props);

    case SettingsSearchItemTypesEnum.RULE:
      return renderRules(resultSection, props);

    case SettingsSearchItemTypesEnum.SIGNATURE:
      return renderSignatures(resultSection, props);

    case SettingsSearchItemTypesEnum.TEAM:
      return renderTeams(resultSection, props);

    case SettingsSearchItemTypesEnum.TEAMMATE:
      return renderTeammates(resultSection, props);

    case SettingsSearchItemTypesEnum.SETTINGS_ROUTE:
    case SettingsSearchItemTypesEnum.COMPANY_PREFERENCE_ROUTE:
    case SettingsSearchItemTypesEnum.COMPANY_SETTINGS_ROUTE:
    case SettingsSearchItemTypesEnum.TEAMMATE_ROUTE:
      return renderRoutes(resultSection, props);

    case SettingsSearchItemTypesEnum.TAG:
      return renderTags(resultSection, props);

    case SettingsSearchItemTypesEnum.VACATION_RESPONDER:
      return renderResponders(resultSection, props);

    case SettingsSearchItemTypesEnum.CHANNEL:
      return renderChannels(resultSection, props);

    case SettingsSearchItemTypesEnum.DELEGATION:
      return renderDelegations(resultSection, props);

    default:
      return null;
  }
}

function maybeRenderSeeAllItems(payload: SettingsSearchItemModel, props: SettingsSearchDropdownProps) {
  if (!payload.seeAllItem) {
    return null;
  }

  return (
    <SettingsSearchDropdownSeeAllItem
      item={payload.seeAllItem}
      onSelect={() => props.onSeeAllClick(payload.resourceType)}
    />
  );
}

function renderDelegations(payload: SettingsSearchDelegationModel, props: SettingsSearchDropdownProps) {
  return payload.items.map((delegationItem, index) => (
    <SettingsSearchDropdownDelegationItem
      key={delegationItem.delegation.id}
      subtitle={delegationItem.description}
      title={delegationItem.name}
      onSelect={() => props.onSelectItem(payload, index)}
    />
  ));
}

function renderChannels(payload: SettingsSearchChannelModel, props: SettingsSearchDropdownProps) {
  const channelsDuplicatesFinder = makeResourceDuplicatesFinder('displayName', payload.items, props.teamsMap);

  return payload.items.map((channel, index) => (
    <SettingsSearchDropdownChannelItem
      key={channel.id}
      channel={channel}
      onSelect={() => props.onSelectItem(payload, index)}
      team={channelsDuplicatesFinder(channel)}
    />
  ));
}

function renderResponders(payload: SettingsSearchVacationResponderModel, props: SettingsSearchDropdownProps) {
  return (
    <>
      {payload.items.map((responderItem, index) => (
        <SettingsSearchDropdownResponderItem
          key={responderItem.vacationResponder.id}
          responder={responderItem.vacationResponder}
          responderDescription={responderItem.channels.map((i) => i.displayName).join(', ')}
          onSelect={() => props.onSelectItem(payload, index)}
        />
      ))}
    </>
  );
}

function renderTags(payload: SettingsSearchTagModel, props: SettingsSearchDropdownProps) {
  const tagsDuplicatesFinder = makeResourceDuplicatesFinder(
    'alias',
    payload.items.map((i) => i.tag),
    props.teamsMap,
  );

  return (
    <>
      {payload.items.map((tagItem, index) => (
        <SettingsSearchDropdownTagItem
          key={tagItem.tag.id}
          tag={tagItem.tag}
          inboxesDescription={tagItem.description}
          onSelect={() => props.onSelectItem(payload, index)}
          team={tagsDuplicatesFinder(tagItem.tag)}
        />
      ))}
    </>
  );
}

function renderCustomViews(
  payload: SettingsSearchCustomViewModel,
  props: SettingsSearchDropdownProps,
  localize: Localizer,
) {
  const viewsDuplicatesFinder = makeResourceDuplicatesFinder('name', payload.items, props.teamsMap);

  return (
    <>
      {payload.items.map((customView, index) => {
        const customViewDescription = customView.rules
          ? computeCustomViewDescription(customView.rules, props.ruleParametersDependencies, localize)
          : customView.description;

        return (
          <SettingsSearchDropdownViewItem
            key={customView.id}
            name={customView.name}
            viewDescription={customViewDescription}
            onSelect={() => props.onSelectItem(payload, index)}
            team={viewsDuplicatesFinder(customView)}
          />
        );
      })}
    </>
  );
}

function renderIntegrations(payload: SettingsSearchIntegrationModel, props: SettingsSearchDropdownProps) {
  return (
    <>
      {payload.items.map((integration, index) => (
        <SettingsSearchDropdownIntegrationItem
          key={integration.type}
          type={integration.type}
          description={integration.description}
          name={integration.name}
          onSelect={() => props.onSelectItem(payload, index)}
        />
      ))}
    </>
  );
}

function renderImports(payload: SettingsSearchImportRouteModel, props: SettingsSearchDropdownProps) {
  return (
    <>
      {payload.items.map((importItem, index) => (
        <SettingsSearchDropdownImportItem
          key={importItem.key}
          itemKey={importItem.key}
          description={importItem.description}
          title={importItem.title}
          onSelect={() => props.onSelectItem(payload, index)}
        />
      ))}
    </>
  );
}

function renderRules(payload: SettingsSearchRuleModel, props: SettingsSearchDropdownProps) {
  const rulesDuplicatesFinder = makeResourceDuplicatesFinder(
    'name',
    payload.items.map((i) => i.rule),
    props.teamsMap,
  );

  return (
    <>
      {payload.items.map((ruleItem, index) => (
        <SettingsSearchDropdownRuleItem
          key={ruleItem.rule.id}
          ruleItem={ruleItem}
          onSelect={() => props.onSelectItem(payload, index)}
          team={rulesDuplicatesFinder(ruleItem.rule)}
          ruleParametersDependencies={props.ruleParametersDependencies}
        />
      ))}
    </>
  );
}

function renderTeams(payload: SettingsSearchTeamModel, props: SettingsSearchDropdownProps) {
  return (
    <>
      {payload.items.map((teamItem, index) => {
        const description = teamItem.inboxes.length ? (
          teamItem.inboxes.map((i) => i.name).join(', ')
        ) : (
          <Trans id="settingssearchdropdown-noinboxes">No inboxes</Trans>
        );

        return (
          <SettingsSearchDropdownTeamItem
            key={teamItem.team.id}
            team={teamItem.team}
            description={description}
            onSelect={() => props.onSelectItem(payload, index)}
          />
        );
      })}
    </>
  );
}

function renderRoles(payload: SettingsSearchRoleModel, props: SettingsSearchDropdownProps) {
  return (
    <>
      {payload.items.map((roleItem, index) => (
        <SettingsSearchDropdownRoleItem
          key={roleItem.role.alias}
          role={roleItem.role}
          teammatesCount={roleItem.teammateCount}
          onSelect={() => props.onSelectItem(payload, index)}
        />
      ))}
    </>
  );
}

function renderSignatures(payload: SettingsSearchSignatureModel, props: SettingsSearchDropdownProps) {
  const signaturesDuplicatesFinder = makeResourceDuplicatesFinder('name', payload.items, props.teamsMap);

  return (
    <>
      {payload.items.map((signature, index) => (
        <SettingsSearchDropdownSignatureItem
          key={signature.id}
          signature={signature}
          onSelect={() => props.onSelectItem(payload, index)}
          team={signaturesDuplicatesFinder(signature)}
        />
      ))}
    </>
  );
}

function renderInboxes(payload: SettingsSearchInboxModel, props: SettingsSearchDropdownProps) {
  const inboxesDuplicatesFinder = makeResourceDuplicatesFinder(
    'alias',
    payload.items.map((i) => i.inbox),
    props.teamsMap,
  );

  return (
    <>
      {payload.items.map((inboxItem, index) => (
        <SettingsSearchDropdownInboxItem
          key={inboxItem.inbox.id}
          inboxItem={inboxItem}
          onSelect={() => props.onSelectItem(payload, index)}
          team={inboxesDuplicatesFinder(inboxItem.inbox)}
        />
      ))}
    </>
  );
}

function renderTeammates(payload: SettingsSearchTeammateModel, props: SettingsSearchDropdownProps) {
  return (
    <>
      {payload.items.map((teammate, index) => (
        <SettingsSearchDropdownTeammateItem
          key={teammate.id}
          onSelect={() => props.onSelectItem(payload, index)}
          teammate={teammate}
        />
      ))}
    </>
  );
}

function renderRoutes(
  payload:
    | SettingsSearchSettingRouteModel
    | SettingsSearchCompanyPreferenceRouteModel
    | SettingsSearchCompanySettingRouteModel
    | SettingsSearchTeammateRouteModel,
  props: SettingsSearchDropdownProps,
) {
  return payload.items.map((routeItem, index) => (
    <SettingsSearchDropdownPreferenceItem
      key={routeItem.key}
      description={routeItem.key === 'myProfile' ? props.user.alias : routeItem.description}
      name={routeItem.title}
      onSelect={() => props.onSelectItem(payload, index)}
      iconName={findIconForSettingRoute(routeItem)}
    />
  ));
}

function renderTeammatePreferences(
  payload: SettingsSearchPersonalPreferenceModel,
  props: SettingsSearchDropdownProps,
) {
  return (
    <>
      {payload.items.map((preference, index) => (
        <SettingsSearchDropdownPreferenceItem
          key={preference.title}
          description={
            preference.key === 'customSnoozes' ? (
              <Trans id="settingssearchdropdown-customsnooze">Custom presets for snooze timings</Trans>
            ) : (
              preference.description
            )
          }
          name={preference.title}
          onSelect={() => props.onSelectItem(payload, index)}
        />
      ))}
    </>
  );
}

function renderCompanyPreference(
  payload: SettingsSearchCompanyPreferenceModel,
  props: SettingsSearchDropdownProps,
) {
  return (
    <>
      {payload.items.map((preference, index) => (
        <SettingsSearchDropdownPreferenceItem
          key={preference.title}
          description={preference.description}
          name={preference.title}
          onSelect={() => props.onSelectItem(payload, index)}
        />
      ))}
    </>
  );
}

function findIconForSettingRoute(routeItem: SettingsSearchRouteItemModel): IconName | undefined {
  const keyToIcon: {[k: string]: IconName} = {
    billing: 'creditcardFilledSmall',
    deliverability: 'sendFilledSmall',
    notifications: 'bellSmall',
    myProfile: 'requiredGuestSmall',
  };

  return keyToIcon[routeItem.key];
}

type DuplicateFields = 'alias' | 'name' | 'displayName';

type DuplicateResource<T extends DuplicateFields> = {
  [key in T]: string;
} & {
  namespace: string;
};
function makeResourceDuplicatesFinder<T extends DuplicateFields>(
  key: T,
  resources: ReadonlyArray<DuplicateResource<T>>,
  teamsMap: ResourceMap<TeamModel>,
) {
  const resourcesAliases = new Set<string>();
  const resourcesAliasDuplicates = new Set<string>();

  resources.forEach((resource) => {
    if (resourcesAliases.has(resource[key])) {
      resourcesAliasDuplicates.add(resource[key]);
      return;
    }

    resourcesAliases.add(resource[key]);
  });

  return (resource: (typeof resources)[number]) =>
    (resourcesAliasDuplicates.has(resource[key]) && teamsMap[resource.namespace]) || undefined;
}
